import { motion } from 'framer-motion';
import { Award, Calendar, Building, ExternalLink } from 'lucide-react';
import { awardsData, affiliationsData } from '../data/siteData';

export default function Awards() {
  return (
    <div data-testid="awards-page">
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-brand-accent">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p className="text-brand-secondary font-semibold tracking-widest uppercase text-sm mb-4">
              Recognition
            </p>
            <h1 className="font-heading text-4xl md:text-6xl font-bold text-brand-primary mb-6">
              Awards & Affiliations
            </h1>
            <p className="text-slate-600 text-lg">
              A collection of recognitions, awards, and professional affiliations that reflect my
              commitment to excellence in leadership development.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Awards Section - Now supports logos */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            className="mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-brand-primary mb-2">
              Awards & Recognition
            </h2>
            <p className="text-slate-600">Honors received throughout my career</p>
          </motion.div>

          <div className="space-y-6">
            {awardsData.map((award, index) => (
              <motion.div
                key={award.id}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group"
                data-testid={`award-${award.id}`}
              >
                <div className="bg-white rounded-2xl border border-slate-100 p-6 md:p-8 hover:shadow-xl hover:border-brand-secondary/20 transition-all duration-300">
                  <div className="flex flex-col md:flex-row md:items-start gap-6">
                    {/* Logo or Icon */}
                    {award.logo ? (
                      <div className="w-20 h-20 rounded-2xl bg-white border border-slate-100 flex items-center justify-center flex-shrink-0 p-2">
                        <img 
                          src={award.logo} 
                          alt={award.organization}
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                    ) : (
                      <div className="w-16 h-16 rounded-2xl bg-brand-secondary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-brand-secondary group-hover:text-white transition-all">
                        <Award className="w-8 h-8 text-brand-secondary group-hover:text-white" />
                      </div>
                    )}

                    {/* Content */}
                    <div className="flex-grow">
                      <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4 mb-2">
                        <h3 className="font-heading text-xl md:text-2xl font-bold text-brand-primary">
                          {award.title}
                        </h3>
                        <span className="inline-flex items-center gap-1 text-sm text-slate-500">
                          <Calendar className="w-4 h-4" />
                          {award.year}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-brand-secondary font-medium mb-3">
                        <Building className="w-4 h-4" />
                        {award.organization}
                      </div>
                      <p className="text-slate-600">{award.description}</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Affiliations Section - Now supports logos */}
      <section className="py-20 md:py-32 bg-brand-primary text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            className="mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-2">
              Professional Affiliations
            </h2>
            <p className="text-white/60">Organizations and institutions I'm proud to be associated with</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {affiliationsData.map((affiliation, index) => (
              <motion.div
                key={affiliation.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group cursor-pointer"
                data-testid={`affiliation-${index}`}
              >
                <div className="bg-white/10 backdrop-blur rounded-2xl p-6 hover:bg-white/20 transition-all duration-300 h-full">
                  <div className="flex items-start justify-between mb-4">
                    {/* Logo or fallback */}
                    {affiliation.logo ? (
                      <div className="w-16 h-16 rounded-xl bg-white flex items-center justify-center p-2">
                        <img 
                          src={affiliation.logo} 
                          alt={affiliation.name}
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                    ) : (
                      <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                        <Building className="w-6 h-6 text-white" />
                      </div>
                    )}
                    <ExternalLink className="w-5 h-5 text-white/40 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="font-semibold text-lg mb-1">{affiliation.name}</h3>
                  <p className="text-brand-secondary font-medium text-sm">{affiliation.role}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Summary Stats */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="font-heading text-4xl md:text-5xl font-bold text-brand-primary mb-2">
                {awardsData.length}+
              </div>
              <div className="text-slate-600 text-sm font-medium">Awards Received</div>
            </motion.div>
            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="font-heading text-4xl md:text-5xl font-bold text-brand-primary mb-2">
                {affiliationsData.length}
              </div>
              <div className="text-slate-600 text-sm font-medium">Affiliations</div>
            </motion.div>
            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <div className="font-heading text-4xl md:text-5xl font-bold text-brand-primary mb-2">
                15+
              </div>
              <div className="text-slate-600 text-sm font-medium">Years of Excellence</div>
            </motion.div>
            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
            >
              <div className="font-heading text-4xl md:text-5xl font-bold text-brand-primary mb-2">
                Global
              </div>
              <div className="text-slate-600 text-sm font-medium">Recognition</div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
